/**
 * Pickup location finder
 */
(function () {
  const ROOT_SELECTOR = '[data-wonderly-pickup-location]';
  const MAP_CALLBACK = '_wonderly_pl_maps_cb';
  const LEAFLET_VERSION = '1.9.4';
  const PIN_PATH =
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';
  const DAY_LABELS = {
    monday: 'Mondays',
    tuesday: 'Tuesdays',
    wednesday: 'Wednesdays',
    thursday: 'Thursdays',
    friday: 'Fridays',
    saturday: 'Saturdays',
    sunday: 'Sundays',
  };
  const DAY_ORDER = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  function waitForZapietConfig(timeoutMs) {
    const timeout = timeoutMs || 20000;
    const start = Date.now();

    return new Promise(function (resolve, reject) {
      function poll() {
        const config =
          window.ZapietCachedSettings && window.ZapietCachedSettings.cached_config;

        if (config && window.Shopify && window.Shopify.shop) {
          resolve(config);
          return;
        }

        if (Date.now() - start > timeout) {
          reject(
            new Error(
              'Zapiet did not load. Enable the Zapiet app embed in Theme settings → App embeds.'
            )
          );
          return;
        }

        requestAnimationFrame(poll);
      }

      poll();
    });
  }

  function decodeGmak(config) {
    if (!config || !config.gmak) return null;

    try {
      return atob(config.gmak);
    } catch (e) {
      return null;
    }
  }

  function isLocalDevHost() {
    const host = window.location.hostname;
    return host === '127.0.0.1' || host === 'localhost';
  }

  function hasMerchantGoogleKey(root) {
    return !!((root && root.dataset.googleMapsApiKey) || '').trim();
  }

  function getMapsApiKey(root, config) {
    const override = (root && root.dataset.googleMapsApiKey) || '';
    if (override.trim()) return override.trim();
    return decodeGmak(config);
  }

  function loadLeaflet() {
    if (window.L && window.L.map) {
      return Promise.resolve(window.L);
    }

    if (window._wonderlyDzLeafletLoading) {
      return window._wonderlyDzLeafletLoading;
    }

    window._wonderlyDzLeafletLoading = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-wonderly-dz-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href =
          'https://unpkg.com/leaflet@' +
          LEAFLET_VERSION +
          '/dist/leaflet.css';
        link.setAttribute('data-wonderly-dz-leaflet', '');
        document.head.appendChild(link);
      }

      const script = document.createElement('script');
      script.src =
        'https://unpkg.com/leaflet@' + LEAFLET_VERSION + '/dist/leaflet.js';
      script.async = true;
      script.onload = function () {
        if (window.L && window.L.map) {
          resolve(window.L);
        } else {
          reject(new Error('Leaflet failed to initialize.'));
        }
      };
      script.onerror = function () {
        window._wonderlyDzLeafletLoading = null;
        reject(new Error('Leaflet script failed to load.'));
      };
      document.head.appendChild(script);
    });

    return window._wonderlyDzLeafletLoading;
  }

  function geocodeZipNominatim(zip, country) {
    const params = new URLSearchParams({
      postalcode: zip,
      countrycodes: country.toLowerCase(),
      format: 'json',
      limit: '1',
    });

    return fetch(
      'https://nominatim.openstreetmap.org/search?' + params.toString(),
      {
        headers: {
          Accept: 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Geocode request failed.');
        }

        return response.json();
      })
      .then(function (results) {
        if (!results || !results[0]) {
          return null;
        }

        return {
          lat: parseFloat(results[0].lat),
          lng: parseFloat(results[0].lon),
        };
      })
      .catch(function () {
        return null;
      });
  }

  function makeLeafletPin(L, color, height) {
    const width = Math.round(height * 0.72);

    return L.divIcon({
      className: 'wonderly-pickup-location__leaflet-pin-icon',
      html:
        '<svg xmlns="http://www.w3.org/2000/svg" width="' +
        width +
        '" height="' +
        height +
        '" viewBox="0 0 24 24" aria-hidden="true"><path d="' +
        PIN_PATH +
        '" fill="' +
        color +
        '" stroke="#222222" stroke-width="1"/></svg>',
      iconSize: [width, height],
      iconAnchor: [width / 2, height],
      popupAnchor: [0, -height + 4],
    });
  }

  function mapLoadErrorMessage(root, detail) {
    const base =
      root.dataset.mapLoadError ||
      'Map could not load. Pickup location details are still shown.';
    const localHint = root.dataset.mapLocalDevHint || '';

    if (isLocalDevHost() && localHint) {
      return base + ' ' + localHint;
    }

    if (detail) {
      return base + ' (' + detail + ')';
    }

    return base;
  }

  function googleMapsReady() {
    return window.google && window.google.maps && window.google.maps.Map;
  }

  function chainGmAuthFailure(onFailure) {
    const prior = window.gm_authFailure;

    window.gm_authFailure = function () {
      window._wonderlyPlMapsAuthFailed = true;
      onFailure();
      if (typeof prior === 'function' && prior !== window.gm_authFailure) {
        prior();
      }
    };
  }

  function loadGoogleMaps(config, root, timeoutMs) {
    const timeout = timeoutMs || 20000;
    const apiKey = getMapsApiKey(root, config);

    if (!apiKey) {
      return Promise.reject(new Error('Google Maps API key not available from Zapiet.'));
    }

    const useExistingMaps =
      googleMapsReady() &&
      !window._wonderlyPlMapsAuthFailed &&
      (!root.dataset.googleMapsApiKey || window._wonderlyPlMapsKeyUsed === apiKey);

    if (useExistingMaps) {
      return Promise.resolve(window.google.maps);
    }

    if (window._wonderlyPlMapsLoading) {
      return window._wonderlyPlMapsLoading;
    }

    window._wonderlyPlMapsAuthFailed = false;

    window._wonderlyPlMapsLoading = new Promise(function (resolve, reject) {
      const timer = window.setTimeout(function () {
        reject(new Error('Google Maps timed out.'));
      }, timeout);

      function failAuth() {
        window.clearTimeout(timer);
        window._wonderlyPlMapsLoading = null;
        reject(
          new Error(
            'Google Maps authentication failed (RefererNotAllowedMapError).'
          )
        );
      }

      chainGmAuthFailure(failAuth);

      window[MAP_CALLBACK] = function () {
        if (window._wonderlyPlMapsAuthFailed) {
          return;
        }

        window.clearTimeout(timer);
        delete window[MAP_CALLBACK];
        window._wonderlyPlMapsLoading = null;
        window._wonderlyPlMapsKeyUsed = apiKey;

        if (googleMapsReady()) {
          resolve(window.google.maps);
        } else {
          reject(new Error('Google Maps failed to initialize.'));
        }
      };

      const script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&v=weekly&loading=async&callback=' +
        MAP_CALLBACK;
      script.async = true;
      script.defer = true;
      script.onerror = function () {
        window.clearTimeout(timer);
        window._wonderlyPlMapsLoading = null;
        reject(new Error('Google Maps script failed to load.'));
      };
      document.head.appendChild(script);
    });

    return window._wonderlyPlMapsLoading;
  }

  function waitForGoogleMaps(config, root, timeoutMs) {
    const pollMs = 2500;
    const timeout = timeoutMs || 20000;
    const start = Date.now();
    const overrideKey = (root && root.dataset.googleMapsApiKey) || '';

    return new Promise(function (resolve, reject) {
      function poll() {
        const canUseExisting =
          googleMapsReady() &&
          !window._wonderlyPlMapsAuthFailed &&
          (!overrideKey || window._wonderlyPlMapsKeyUsed === overrideKey.trim());

        if (canUseExisting) {
          resolve(window.google.maps);
          return;
        }

        if (Date.now() - start > pollMs) {
          loadGoogleMaps(config, root, timeout).then(resolve).catch(reject);
          return;
        }

        requestAnimationFrame(poll);
      }

      poll();
    });
  }

  function verifyMapTiles(map, maps, onFailure) {
    let tilesLoaded = false;

    maps.event.addListenerOnce(map, 'tilesloaded', function () {
      tilesLoaded = true;
    });

    window.setTimeout(function () {
      if (!tilesLoaded || window._wonderlyPlMapsAuthFailed) {
        onFailure();
      }
    }, 4000);
  }

  function pickupLocationsUrl(zip) {
    const shop = window.Shopify.shop;
    const query = encodeURIComponent(zip);

    return (
      'https://api-us.zapiet.com/v1.0/pickup/locations?shop=' +
      shop +
      '&geoSearchQuery=' +
      query
    );
  }

  function fetchPickupLocations(zip) {
    return fetch(pickupLocationsUrl(zip))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        const locations = Array.isArray(data.locations) ? data.locations : [];

        return {
          eligible: locations.length > 0,
          locations: locations,
          closest: locations[0] || null,
          raw: data,
        };
      });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatTime12h(time24) {
    if (!time24 || typeof time24 !== 'string') return '';

    const parts = time24.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const period = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12 || 12;

    if (!minutes) {
      return hours + ' ' + period;
    }

    return hours + ':' + String(minutes).padStart(2, '0') + ' ' + period;
  }

  function formatPickupHours(location, prefix) {
    prefix = prefix || 'Pickup: ';
    const openingHours = location && location.opening_hours;

    if (!openingHours || typeof openingHours !== 'object') {
      if (location && location.more_information) {
        return prefix + String(location.more_information).trim();
      }
      return '';
    }

    const entries = [];

    DAY_ORDER.forEach(function (day) {
      const slot = openingHours[day];
      if (!slot || slot.closed) return;

      entries.push({
        day: DAY_LABELS[day] || day,
        hours:
          formatTime12h(slot.opens) + '\u2013' + formatTime12h(slot.closes),
      });
    });

    if (!entries.length) return '';

    const groups = {};

    entries.forEach(function (entry) {
      if (!groups[entry.hours]) {
        groups[entry.hours] = [];
      }
      groups[entry.hours].push(entry.day);
    });

    const parts = Object.keys(groups).map(function (hours) {
      const days = groups[hours];
      const dayLabel = days.length === 1 ? days[0] : days.join(', ');
      return dayLabel + ' ' + hours;
    });

    return prefix + parts.join('; ');
  }

  function formatCityRegion(location) {
    return [location.city, location.region_code || location.region]
      .filter(Boolean)
      .join(', ');
  }

  function locationDetailsHtml(location) {
    const lines = [
      location.company_name,
      location.address_line_1,
      formatCityRegion(location),
    ].filter(Boolean);

    return lines
      .map(function (line) {
        return '<strong>' + escapeHtml(line) + '</strong>';
      })
      .join('<br>');
  }

  function locationPopupHtml(location) {
    const cityLine = formatCityRegion(location);

    return (
      '<div class="wonderly-pickup-location__map-info">' +
      '<strong>' +
      escapeHtml(location.company_name) +
      '</strong><br>' +
      escapeHtml(location.address_line_1 || '') +
      (cityLine ? '<br>' + escapeHtml(cityLine) : '') +
      '</div>'
    );
  }

  function makePinIcon(maps, color, scale) {
    return {
      path: PIN_PATH,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#222222',
      strokeWeight: 1,
      scale: scale || 1.4,
      anchor: new maps.Point(12, 22),
    };
  }

  function locationMapCardAddressHtml(location) {
    const lines = [location.address_line_1, formatCityRegion(location)].filter(Boolean);

    return lines.map(function (line) {
      return escapeHtml(line);
    }).join('<br>');
  }

  function renderMapDetailsCard(root, location) {
    const nameEl = root.querySelector('[data-pl-details-name]');
    const addressEl = root.querySelector('[data-pl-details-address]');

    if (nameEl) {
      nameEl.textContent = location.company_name || '';
    }

    if (addressEl) {
      addressEl.innerHTML = locationMapCardAddressHtml(location);
    }
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }

  function resetDetailsCardPosition(root) {
    const card = root.querySelector('[data-pl-details]');
    if (!card) return;

    card.style.left = '';
    card.style.top = '';
    card.classList.remove('wonderly-pickup-location__details--below');
  }

  function unbindDetailsCardPosition(root) {
    const instance = root._plMap;

    if (instance && instance.detailsPositionCleanup) {
      instance.detailsPositionCleanup();
      instance.detailsPositionCleanup = null;
    }

    resetDetailsCardPosition(root);
  }

  function bindDetailsCardPosition(root) {
    const instance = root._plMap;
    const card = root.querySelector('[data-pl-details]');
    const overlayEl = root.querySelector('[data-pl-map-overlay]');
    const mapEl = root.querySelector('[data-pl-map]');

    unbindDetailsCardPosition(root);

    if (!instance || !card || card.hidden || !mapEl || !overlayEl || !instance.closestLatLng) {
      return;
    }

    const inset = 16;
    const padding = 8;
    const closestPinHeight = 34;
    const tailSize = 12;

    function updatePosition() {
      if (!root._plMap || card.hidden) return;

      const cardWidth = card.offsetWidth;
      const cardHeight = card.offsetHeight;
      const overlayWidth = overlayEl.offsetWidth;
      const overlayHeight = overlayEl.offsetHeight;

      if (!cardWidth || !cardHeight || !overlayWidth || !overlayHeight) {
        return;
      }

      let point;

      if (instance.provider === 'leaflet') {
        point = instance.map.latLngToContainerPoint([
          instance.closestLatLng.lat,
          instance.closestLatLng.lng,
        ]);
      } else if (instance.detailsOverlay) {
        const projection = instance.detailsOverlay.getProjection();
        if (!projection) return;

        point = projection.fromLatLngToDivPixel(
          new window.google.maps.LatLng(
            instance.closestLatLng.lat,
            instance.closestLatLng.lng
          )
        );
      } else {
        return;
      }

      const anchorX = point.x - inset;
      const anchorY = point.y - inset;

      let left = anchorX - cardWidth / 2;
      let top = anchorY - closestPinHeight - cardHeight - 10;
      const placeBelow = top < padding;

      card.classList.toggle('wonderly-pickup-location__details--below', placeBelow);

      if (placeBelow) {
        top = anchorY + closestPinHeight - 10;
      }

      const bottomReserve = placeBelow ? padding : padding + tailSize;

      left = clamp(left, padding, overlayWidth - cardWidth - padding);
      top = clamp(top, padding + (placeBelow ? tailSize : 0), overlayHeight - cardHeight - bottomReserve);

      card.style.left = left + 'px';
      card.style.top = top + 'px';
    }

    function scheduleUpdate() {
      window.requestAnimationFrame(updatePosition);
    }

    const cleanups = [];

    if (instance.provider === 'leaflet') {
      instance.map.on('move zoom resize', scheduleUpdate);
      cleanups.push(function () {
        instance.map.off('move zoom resize', scheduleUpdate);
      });
    } else if (instance.map && window.google && window.google.maps) {
      const idleListener = window.google.maps.event.addListener(instance.map, 'idle', scheduleUpdate);
      const boundsListener = window.google.maps.event.addListener(
        instance.map,
        'bounds_changed',
        scheduleUpdate
      );

      cleanups.push(function () {
        window.google.maps.event.removeListener(idleListener);
        window.google.maps.event.removeListener(boundsListener);
      });
    }

    function onResize() {
      scheduleUpdate();
    }

    window.addEventListener('resize', onResize);
    cleanups.push(function () {
      window.removeEventListener('resize', onResize);
    });

    instance.detailsPositionCleanup = function () {
      cleanups.forEach(function (cleanup) {
        cleanup();
      });
    };

    scheduleUpdate();
    window.setTimeout(scheduleUpdate, 300);
    window.setTimeout(scheduleUpdate, 700);
  }

  function getClosestLatLng(validLocations) {
    const closest = validLocations[0];
    if (!closest || !closest.latitude || !closest.longitude) return null;

    return {
      lat: Number(closest.latitude),
      lng: Number(closest.longitude),
    };
  }

  function resetMapElement(mapEl) {
    if (!mapEl) return;

    mapEl.hidden = false;
    mapEl.innerHTML = '';
    mapEl.className = 'wonderly-pickup-location__map';
    mapEl.removeAttribute('style');
  }

  function attachGoogleDetailsOverlay(map) {
    if (!window.google || !window.google.maps) return null;

    const overlay = new window.google.maps.OverlayView();
    overlay.onAdd = function () {};
    overlay.draw = function () {};
    overlay.setMap(map);

    return overlay;
  }

  function clearMap(root) {
    const instance = root._plMap;
    if (!instance) return;

    unbindDetailsCardPosition(root);

    if (instance.provider === 'leaflet') {
      try {
        instance.map.remove();
      } catch (e) {
        /* Leaflet may already be torn down */
      }
    } else {
      instance.markers.forEach(function (marker) {
        marker.setMap(null);
      });

      if (instance.infoWindow) {
        instance.infoWindow.close();
      }

      if (instance.detailsOverlay) {
        instance.detailsOverlay.setMap(null);
      }
    }

    root._plMap = null;
  }

  function showMapFallback(root, message) {
    const mapEl = root.querySelector('[data-pl-map]');
    const fallbackEl = root.querySelector('[data-pl-map-fallback]');
    const mapPanel = root.querySelector('[data-pl-map-panel]');
    const detailsCard = root.querySelector('[data-pl-details]');
    const mapOverlay = root.querySelector('[data-pl-map-overlay]');

    if (mapEl) mapEl.hidden = true;
    if (detailsCard) detailsCard.hidden = true;
    if (mapOverlay) mapOverlay.hidden = true;
    if (fallbackEl) {
      fallbackEl.hidden = false;
      fallbackEl.textContent = message;
    }
    if (mapPanel) mapPanel.hidden = false;
  }

  function getMapRenderContext(root, result, config) {
    const country = ((config && config.base_country_code) || 'US').toLowerCase();
    const pinColor =
      getComputedStyle(root).getPropertyValue('--wonderly-pl-map-pin').trim() ||
      root.dataset.mapPinColor ||
      '#fd3d00';
    const locations = result.locations || [];

    return {
      country: country,
      pinColor: pinColor,
      customerLabel: root.dataset.mapCustomerLabel || 'Your search area',
      geocodeError: root.dataset.mapGeocodeError || 'Map unavailable for this ZIP.',
      validLocations: locations.filter(function (location) {
        return location.latitude && location.longitude;
      }),
    };
  }

  function renderLeafletMap(root, result, config) {
    const mapEl = root.querySelector('[data-pl-map]');
    const fallbackEl = root.querySelector('[data-pl-map-fallback]');

    if (!mapEl) return Promise.resolve();

    clearMap(root);
    resetMapElement(mapEl);

    const ctx = getMapRenderContext(root, result, config);

    return loadLeaflet()
      .then(function (L) {
        return geocodeZipNominatim(result.zip, ctx.country).then(function (customerLatLng) {
          if (!customerLatLng && !ctx.validLocations.length) {
            showMapFallback(root, ctx.geocodeError);
            return;
          }

          const map = L.map(mapEl, {
            scrollWheelZoom: false,
          });

          L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          }).addTo(map);

          const bounds = L.latLngBounds([]);
          const markers = [];

          if (customerLatLng) {
            const customerMarker = L.marker([customerLatLng.lat, customerLatLng.lng], {
              icon: makeLeafletPin(L, '#222222', 26),
              title: ctx.customerLabel,
            }).addTo(map);

            customerMarker.bindTooltip(ctx.customerLabel);
            bounds.extend([customerLatLng.lat, customerLatLng.lng]);
            markers.push(customerMarker);
          }

          ctx.validLocations.forEach(function (location, index) {
            const lat = Number(location.latitude);
            const lng = Number(location.longitude);
            const isClosest = index === 0;
            const marker = L.marker([lat, lng], {
              icon: makeLeafletPin(L, ctx.pinColor, isClosest ? 34 : 28),
              title: location.company_name || '',
              zIndexOffset: isClosest ? 900 : 100 + index,
            }).addTo(map);

            if (!isClosest) {
              marker.bindPopup(locationPopupHtml(location));
            }

            bounds.extend([lat, lng]);
            markers.push(marker);
          });

          if (bounds.isValid()) {
            if (markers.length === 1) {
              map.setView(bounds.getCenter(), 11);
            } else {
              map.fitBounds(bounds, { padding: [32, 32] });
            }
          }

          root._plMap = {
            provider: 'leaflet',
            map: map,
            markers: markers,
            closestLatLng: getClosestLatLng(ctx.validLocations),
          };

          if (fallbackEl) fallbackEl.hidden = true;

          window.setTimeout(function () {
            map.invalidateSize();
            if (bounds.isValid() && markers.length > 1) {
              map.fitBounds(bounds, { padding: [32, 32] });
            }
            bindDetailsCardPosition(root);
          }, 250);
        });
      })
      .catch(function () {
        showMapFallback(root, mapLoadErrorMessage(root));
      });
  }

  function renderGoogleMap(root, result, maps, config) {
    const mapEl = root.querySelector('[data-pl-map]');
    const fallbackEl = root.querySelector('[data-pl-map-fallback]');

    if (!mapEl) return Promise.resolve();

    clearMap(root);
    resetMapElement(mapEl);

    const ctx = getMapRenderContext(root, result, config);

    function buildMap(customerLatLng) {
      if (!customerLatLng && !ctx.validLocations.length) {
        showMapFallback(root, ctx.geocodeError);
        return;
      }

      const map = new maps.Map(mapEl, {
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: maps.ControlPosition.TOP_LEFT,
        },
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      const bounds = new maps.LatLngBounds();
      const markers = [];
      const infoWindow = new maps.InfoWindow();

      if (customerLatLng) {
        const customerMarker = new maps.Marker({
          map: map,
          position: customerLatLng,
          title: ctx.customerLabel,
          icon: makePinIcon(maps, '#222222', 1.3),
          zIndex: 1000,
        });
        markers.push(customerMarker);
        bounds.extend(customerLatLng);
      }

      ctx.validLocations.forEach(function (location, index) {
        const position = {
          lat: Number(location.latitude),
          lng: Number(location.longitude),
        };
        const isClosest = index === 0;
        const marker = new maps.Marker({
          map: map,
          position: position,
          title: location.company_name || '',
          icon: makePinIcon(maps, ctx.pinColor, isClosest ? 1.7 : 1.35),
          zIndex: isClosest ? 900 : 100 + index,
        });

        const content = locationPopupHtml(location);

        if (!isClosest) {
          marker.addListener('click', function () {
            infoWindow.setContent(content);
            infoWindow.open(map, marker);
          });
        }

        markers.push(marker);
        bounds.extend(position);
      });

      if (bounds.isEmpty()) {
        showMapFallback(root, ctx.geocodeError);
        return;
      }

      if (markers.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(11);
      } else {
        map.fitBounds(bounds, 56);
      }

      const detailsOverlay = attachGoogleDetailsOverlay(map);

      root._plMap = {
        provider: 'google',
        map: map,
        markers: markers,
        infoWindow: infoWindow,
        detailsOverlay: detailsOverlay,
        closestLatLng: getClosestLatLng(ctx.validLocations),
      };

      mapEl.hidden = false;
      if (fallbackEl) fallbackEl.hidden = true;

      verifyMapTiles(map, maps, function () {
        clearMap(root);
        renderLeafletMap(root, result, config);
      });

      window.setTimeout(function () {
        maps.event.trigger(map, 'resize');
        if (markers.length > 1) {
          map.fitBounds(bounds, 56);
        }
        bindDetailsCardPosition(root);
      }, 250);
    }

    return new Promise(function (resolve) {
      if (!window.google || !window.google.maps) {
        renderLeafletMap(root, result, config).then(resolve);
        return;
      }

      const geocoder = new maps.Geocoder();

      geocoder.geocode(
        {
          address: result.zip,
          componentRestrictions: { country: ctx.country },
        },
        function (geoResults, status) {
          let customerLatLng = null;

          if (status === 'OK' && geoResults && geoResults[0]) {
            customerLatLng = geoResults[0].geometry.location;
          }

          buildMap(customerLatLng);
          resolve();
        }
      );
    });
  }

  function renderResultsMap(root, result, config) {
    return new Promise(function (resolve) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (hasMerchantGoogleKey(root)) {
            waitForGoogleMaps(config, root)
              .then(function (maps) {
                return renderGoogleMap(root, result, maps, config);
              })
              .catch(function () {
                return renderLeafletMap(root, result, config);
              })
              .then(resolve);
            return;
          }

          renderLeafletMap(root, result, config).then(resolve);
        });
      });
    });
  }

  function setState(root, state) {
    root.dataset.state = state;
    root.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    root.querySelectorAll('[data-pl-panel]').forEach(function (panel) {
      panel.hidden = panel.dataset.plPanel !== state;
    });
  }

  function renderResults(root, result) {
    const resultsCopy = root.querySelector('[data-pl-results-copy]');
    const detailsCard = root.querySelector('[data-pl-details]');
    const noResultsEl = root.querySelector('[data-pl-no-results]');
    const hoursEl = root.querySelector('[data-pl-details-hours]');
    const locationEl = root.querySelector('[data-pl-details-location]');
    const shopCta = root.querySelector('[data-pl-shop-cta]');
    const mapPanel = root.querySelector('[data-pl-map-panel]');
    const hoursPrefix = root.dataset.pickupHoursPrefix || 'Pickup: ';

    if (result.eligible && result.closest) {
      if (resultsCopy) resultsCopy.hidden = false;
      if (detailsCard) detailsCard.hidden = false;
      if (noResultsEl) noResultsEl.hidden = true;
      if (locationEl) {
        locationEl.innerHTML = locationDetailsHtml(result.closest);
      }
      renderMapDetailsCard(root, result.closest);
      if (hoursEl) {
        const hours = formatPickupHours(result.closest, hoursPrefix);
        if (hours) {
          hoursEl.textContent = hours;
          hoursEl.hidden = false;
        } else {
          hoursEl.textContent = '';
          hoursEl.hidden = true;
        }
      }
      if (shopCta) shopCta.hidden = false;
      if (mapPanel) mapPanel.hidden = false;
    } else {
      if (resultsCopy) resultsCopy.hidden = true;
      if (detailsCard) detailsCard.hidden = true;
      if (noResultsEl) noResultsEl.hidden = false;
      if (hoursEl) {
        hoursEl.textContent = '';
        hoursEl.hidden = true;
      }
      if (shopCta) shopCta.hidden = true;
      if (mapPanel) mapPanel.hidden = true;
    }
  }

  function init(root) {
    const form = root.querySelector('[data-pl-form]');
    const input = root.querySelector('[data-pl-zip]');
    const errorEl = root.querySelector('[data-pl-error]');
    const debugEl = root.querySelector('[data-pl-debug]');
    const showDebug = root.dataset.showDebug === 'true';

    if (!form || !input) return;

    input.addEventListener('input', function () {
      if (window.WonderlyZipCheck) {
        input.value = window.WonderlyZipCheck.sanitizeInput(input.value);
      }
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      const zip = window.WonderlyZipCheck
        ? window.WonderlyZipCheck.normalize(input.value)
        : (input.value || '').trim();

      if (!zip) {
        input.focus();
        return;
      }

      if (window.WonderlyZipCheck && !window.WonderlyZipCheck.isValid(zip)) {
        if (errorEl) {
          errorEl.textContent =
            root.dataset.invalidZipMessage || 'Please enter a valid 5-digit ZIP Code.';
        }
        input.focus();
        return;
      }

      if (errorEl) errorEl.textContent = '';
      if (debugEl) debugEl.textContent = '';
      clearMap(root);
      setState(root, 'loading');

      let zapietConfig;

      waitForZapietConfig()
        .then(function (config) {
          zapietConfig = config;
          return fetchPickupLocations(zip).then(function (pickup) {
            return {
              zip: zip,
              eligible: pickup.eligible,
              locations: pickup.locations,
              closest: pickup.closest,
              raw: pickup.raw,
            };
          });
        })
        .then(function (result) {
          renderResults(root, result);
          setState(root, 'results');

          if (showDebug && debugEl) {
            try {
              debugEl.textContent = JSON.stringify(result, null, 2);
            } catch (e) {
              debugEl.textContent = String(result);
            }
          }

          root._plLastResult = result;
          root._plZapietConfig = zapietConfig;

          if (result.eligible) {
            return renderResultsMap(root, result, zapietConfig);
          }

          return undefined;
        })
        .catch(function (err) {
          const isMapOnly =
            err &&
            err.message &&
            (err.message.indexOf('Google Maps') !== -1 ||
              err.message.indexOf('Referer') !== -1 ||
              err.message.indexOf('Leaflet') !== -1 ||
              err.message.indexOf('localhost') !== -1);

          if (isMapOnly && root.dataset.state === 'results' && root._plLastResult) {
            renderLeafletMap(root, root._plLastResult, root._plZapietConfig);
            if (showDebug && debugEl) {
              debugEl.textContent = String(err.stack || err.message || err);
            }
            return;
          }

          if (errorEl) {
            errorEl.textContent =
              err && err.message ? err.message : 'Something went wrong. Please try again.';
          }
          if (showDebug && debugEl && err) {
            debugEl.textContent = String(err.stack || err.message || err);
          }
          setState(root, 'form');
        });
    });

    const queryZip = new URLSearchParams(window.location.search).get('zip');
    if (queryZip) {
      input.value = queryZip.trim();
      if (input.value) {
        form.requestSubmit();
      }
    }
  }

  function boot() {
    if (!window._wonderlyPlGmAuthHooked) {
      window._wonderlyPlGmAuthHooked = true;
      chainGmAuthFailure(function () {});
    }

    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
